import React, { type FC, useCallback, useEffect, useState } from 'react';
import { FinalDecisionMakingProperty, ListSortProps } from '../../utility/types';
import FinalDecisionMakingTable from './FinalDecisionMakingTable';
import FinalDecisionExportPdfButton from './FinalDecisionExportPdfButton';
import FinalDecisionActionsMenu from './FinalDecisionActionsMenu';
import ContractReviewModal from './ContractReviewModal';
import { useStoredContractOffers } from '../../hooks/api/ContractOffers/useContractOffers';
import { groupOffersByProperty } from '../../utility/contractOffers';
import { LISTING_EMAIL_BULK_MAX, downloadWholesaleOffers, listingEmailBulkUrl, wholesaleOfferDraftsUrl } from '../../utility/listingEmail';
import { useListingEmailStatuses } from '../../hooks/api/ListingEmails/useListingEmails';
import useGetFinalDecisionMakingProperties from '../../hooks/api/FinalDecisionMaking/useGetFinalDecisionMakingProperties';
import { LoadingSpinner } from '../icons/OurIcons';
import { formatPropertiesForFDMTable } from '../../utility/utility_functions';

const SHOW_CONTRACTS_KEY = 'fdmShowContracts';

export type FinalDecisionMakingContainerProps = {
  activeListSort: ListSortProps;
}

const FinalDecisionMakingContainer: FC<FinalDecisionMakingContainerProps> = ({
  activeListSort,
}) => {
  const {
    finalDecisionMakingProperties,
    propertyAuctionNumbers,
    isLoading,
    errorFinalDecisionMakingProperties,
  } = useGetFinalDecisionMakingProperties(activeListSort?.id);

  const formattedProperties = formatPropertiesForFDMTable(finalDecisionMakingProperties, propertyAuctionNumbers);

  // Rows in the order the table is currently showing them (after the user's sorting)
  const [ displayedRows, setDisplayedRows ] = useState<FinalDecisionMakingProperty[] | null>(null);

  const handleDisplayedRowsChange = useCallback((rows: FinalDecisionMakingProperty[]) => {
    setDisplayedRows((prev) => (
      prev && prev.length === rows.length && rows.every((row, i) => row === prev[i]) ? prev : rows
    ));
  }, []);

  // A different list means a fresh table with no sort applied
  useEffect(() => {
    setDisplayedRows(null);
  }, [activeListSort?.id]);

  // Fall back to API order if the displayed rows no longer match the data (e.g. a row was deleted)
  const exportRows = displayedRows && displayedRows.length === formattedProperties?.length ? displayedRows : formattedProperties;

  // Contracts already stored for this list's properties (visible to the buyer and the pro who made them)
  const { offers } = useStoredContractOffers(activeListSort?.id);
  const offersByProperty = groupOffersByProperty(offers);

  // The Contracts column can be hidden; the choice is remembered in this browser
  const [ showContracts, setShowContracts ] = useState(() => {
    try {
      return window.localStorage.getItem(SHOW_CONTRACTS_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const toggleContracts = () => {
    setShowContracts((current) => {
      try {
        window.localStorage.setItem(SHOW_CONTRACTS_KEY, String(!current));
      } catch {
        // storage unavailable: the choice just lasts for this visit
      }
      return !current;
    });
  };

  // Stripe sends people back here after paying for credits
  const creditsNotice = new URLSearchParams(window.location.search).get('credits') === 'success';

  // Automatic Contracting: checkboxes beside each address, then a review screen for the checked ones
  const [ contractMode, setContractMode ] = useState(false);
  const [ selectedIds, setSelectedIds ] = useState<string[]>([]);
  const [ reviewOpen, setReviewOpen ] = useState(false);

  const toggleSelected = (propertyId: string) => {
    setSelectedIds((current) => (current.includes(propertyId) ? current.filter((id) => id !== propertyId) : [ ...current, propertyId ]));
  };
  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? (formattedProperties ?? []).map((p) => String(p.propertyId)) : []);
  };
  const exitContractMode = () => {
    setContractMode(false);
    setEmailMode(false);
    setDraftMode(false);
    setOfferMode(false);
    setSelectedIds([]);
    setReviewOpen(false);
  };
  const selectedRows = (exportRows ?? []).filter((row) => selectedIds.includes(String(row.propertyId)));

  // Email Listing Agent: a button beside each address that downloads a draft email with the contract attached
  const [ emailMode, setEmailMode ] = useState(false);
  // Draft Email: a general email draft per selected property (no offer, no listing agent)
  const [ draftMode, setDraftMode ] = useState(false);
  // Make Wholesale Offer: an email to the wholesaler offering the Max Bid (no contract)
  const [ offerMode, setOfferMode ] = useState(false);
  const [ offerError, setOfferError ] = useState<string | null>(null);
  const [ offerBusy, setOfferBusy ] = useState(false);
  const sendOffers = async () => {
    if (!activeListSort) return;
    setOfferError(null);
    setOfferBusy(true);
    try {
      await downloadWholesaleOffers(wholesaleOfferDraftsUrl(activeListSort.id, selectedIds));
    } catch (err) {
      setOfferError((err as Error).message);
    } finally {
      setOfferBusy(false);
    }
  };
  const { statuses: emailStatuses, emailClient, isLoading: emailLoading, error: emailError } = useListingEmailStatuses(activeListSort?.id, emailMode);

  // A different list starts with no selections
  useEffect(() => {
    exitContractMode();
  }, [ activeListSort?.id ]);

  return (
    <div id='fdm-container' className='max-h-full top-0 pb-10 text-xs px-4 text-xl align-left'>
      <div id='fdm-header' className='py-4 text-xl align-left'>
        {activeListSort ? (
          <div id='fdm-header-container' className='flex justify-between'>
            <div id='fdm-header-title-container' className='pl-2'>
              <span className="text-gray-700">List: </span>
              <span className='pl-2 font-bold uppercase underline'>{activeListSort.title}</span>
            </div>
            <div id='fdm-export-pdf-button-container' className='flex items-center gap-x-2 pr-2'>
              {offers.length > 0 && (
                <button
                  type='button'
                  id='fdm-contracts-toggle'
                  data-tag='fdm-contracts-toggle'
                  className='rounded bg-white px-3 py-2 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                  onClick={toggleContracts}
                >
                  {showContracts ? 'Hide contracts' : 'Show contracts'}
                </button>
              )}
              <FinalDecisionActionsMenu
                disabled={isLoading || !!errorFinalDecisionMakingProperties || !formattedProperties?.length}
                onSelectContracting={() => { setEmailMode(false); setDraftMode(false); setOfferMode(false); setContractMode(true); }}
                onSelectEmail={() => { setContractMode(false); setDraftMode(false); setOfferMode(false); setSelectedIds([]); setReviewOpen(false); setEmailMode(true); }}
                onSelectDraft={() => { setContractMode(false); setEmailMode(false); setOfferMode(false); setSelectedIds([]); setReviewOpen(false); setDraftMode(true); }}
                onSelectWholesaleOffer={() => { setContractMode(false); setEmailMode(false); setDraftMode(false); setSelectedIds([]); setReviewOpen(false); setOfferMode(true); }}
              />
              <FinalDecisionExportPdfButton
                properties={isLoading || errorFinalDecisionMakingProperties ? [] : exportRows}
                listTitle={activeListSort.title}
              />
            </div>
          </div>
        ) : (
          <span>Please select a list sort to view the properties ready for Final Decision</span>
        )}
      </div>

      {creditsNotice && (
        <div id='fdm-credits-notice' role='status' className='mb-3 rounded-md bg-green-50 px-4 py-2 text-sm text-green-800'>
          Payment received. Your credits are added as soon as Stripe confirms it, usually within a minute.
        </div>
      )}

      {contractMode && (
        <div id='fdm-contract-bar' className='mb-3 flex items-center justify-between rounded-md bg-indigo-50 px-4 py-2 text-sm text-indigo-900'>
          <span>Automatic Contracting: check the properties to make contracts for.</span>
          <div className='flex items-center gap-x-2'>
            <button
              type='button'
              data-tag='fdm-review-contracts-button'
              disabled={selectedRows.length === 0}
              className='rounded bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50'
              onClick={() => setReviewOpen(true)}
            >
              Review ({selectedRows.length})
            </button>
            <button
              type='button'
              className='rounded bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
              onClick={exitContractMode}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {emailMode && (
        <div id='fdm-email-bar' className='mb-3 flex items-center justify-between rounded-md bg-indigo-50 px-4 py-2 text-sm text-indigo-900 print:hidden'>
          <span>
            Email Listing Agent: use the button beside an address, or tick several and download all the drafts at once. The newest contract is attached when there is one; without one, the email uses your template and standing documents.
            {emailLoading && ' Checking listing agents...'}
            {emailError && ' We could not check the listing agents right now.'}
          </span>
          <div className='flex items-center gap-x-2'>
            <a
              data-tag='email-selected-button'
              aria-disabled={selectedIds.length === 0 || selectedIds.length > LISTING_EMAIL_BULK_MAX}
              href={selectedIds.length === 0 || selectedIds.length > LISTING_EMAIL_BULK_MAX || !activeListSort ? undefined : listingEmailBulkUrl(activeListSort.id, selectedIds)}
              download
              title={`Download a draft email for each selected property (up to ${LISTING_EMAIL_BULK_MAX} at a time)`}
              className={`rounded px-3 py-2 text-xs font-semibold text-white ${selectedIds.length === 0 || selectedIds.length > LISTING_EMAIL_BULK_MAX ? 'cursor-not-allowed bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-500'}`}
            >
              Email selected ({selectedIds.length})
            </a>
            <a
              href='/configurations'
              className='rounded bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
              title='Edit the letter and choose your email app under Configuration > Email Template'
            >
              Edit email template
            </a>
            <button
              type='button'
              className='rounded bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
              onClick={() => setEmailMode(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {draftMode && (
        <div id='fdm-draft-bar' className='mb-3 flex items-center justify-between rounded-md bg-indigo-50 px-4 py-2 text-sm text-indigo-900 print:hidden'>
          <span>
            Draft Email: tick the properties, then download the drafts. Each is a plain email from your email template with your standing
            documents attached, and the To line left empty. No offer or listing agent is needed.
          </span>
          <div className='flex items-center gap-x-2'>
            <a
              data-tag='draft-selected-button'
              aria-disabled={selectedIds.length === 0 || selectedIds.length > LISTING_EMAIL_BULK_MAX}
              href={selectedIds.length === 0 || selectedIds.length > LISTING_EMAIL_BULK_MAX || !activeListSort ? undefined : listingEmailBulkUrl(activeListSort.id, selectedIds, true)}
              download
              title={`Download a draft email for each selected property (up to ${LISTING_EMAIL_BULK_MAX} at a time)`}
              className={`rounded px-3 py-2 text-xs font-semibold text-white ${selectedIds.length === 0 || selectedIds.length > LISTING_EMAIL_BULK_MAX ? 'cursor-not-allowed bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-500'}`}
            >
              Draft selected ({selectedIds.length})
            </a>
            <a
              href='/configurations'
              className='rounded bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
              title='Edit the letter and choose your email app under Configuration > Email Template'
            >
              Edit email template
            </a>
            <button
              type='button'
              className='rounded bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
              onClick={() => { setDraftMode(false); setSelectedIds([]); }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {offerMode && (
        <div id='fdm-offer-bar' className='mb-3 flex items-center justify-between rounded-md bg-indigo-50 px-4 py-2 text-sm text-indigo-900 print:hidden'>
          <span>
            Make Wholesale Offer: tick the wholesale deals, then download the emails. Each is a draft to the wholesaler offering your Max Bid
            (it is not a contract), with your standing documents attached. Set a Max Bid on each property first.
          </span>
          <div className='flex items-center gap-x-2'>
            <button
              type='button'
              data-tag='offer-selected-button'
              disabled={selectedIds.length === 0 || selectedIds.length > LISTING_EMAIL_BULK_MAX || offerBusy}
              onClick={sendOffers}
              title={`Download an offer email for each selected wholesale deal (up to ${LISTING_EMAIL_BULK_MAX} at a time)`}
              className='rounded bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400'
            >
              {offerBusy ? 'Making...' : `Offer selected (${selectedIds.length})`}
            </button>
            <button
              type='button'
              className='rounded bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
              onClick={() => { setOfferMode(false); setOfferError(null); setSelectedIds([]); }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {offerMode && offerError && (
        <p role='alert' data-tag='offer-error' className='mb-3 rounded-md bg-amber-50 px-4 py-2 text-sm text-amber-800 print:hidden'>{offerError}</p>
      )}

      {isLoading && (
        <div id='fdm-loading-container' className='flex flex-col justify-center items-center align-center'>
          <LoadingSpinner />
          <p>Loading...</p>
        </div>
      )}

      {errorFinalDecisionMakingProperties && (
        <div id='fdm-error-container' className='flex justify-center items-center'>
          <p>Error loading properties</p>
        </div>
      )}
      {activeListSort && !isLoading && !errorFinalDecisionMakingProperties && (
        <FinalDecisionMakingTable
          properties={formattedProperties}
          onDisplayedRowsChange={handleDisplayedRowsChange}
          activeListSortId={activeListSort.id}
          listSortPropertyJoins={activeListSort.list_sort_property_joins}
          selectionMode={contractMode || emailMode || draftMode || offerMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelected}
          onToggleAll={toggleAll}
          offersByProperty={offersByProperty}
          showContracts={showContracts}
          emailMode={emailMode}
          emailStatuses={emailStatuses}
          emailClient={emailClient}
        />
      )}
      {reviewOpen && activeListSort && (
        <ContractReviewModal listSortId={activeListSort.id} rows={selectedRows} onClose={() => setReviewOpen(false)} />
      )}
    </div>
  );
};

export default FinalDecisionMakingContainer;
